/**
 * M3 WORLD B3: salvage renderer. A pooled set of scoopable canisters (and mine
 * nodes), placed by floating-origin delta — the same pool pattern as
 * projectiles.ts. The authoritative SalvageNode[] lives in the SignalManager's
 * active encounter; this only maps the remaining (un-scooped) nodes onto pooled
 * meshes each frame. Cheap emissive cubes so traversal visibly pays off (scoop ->
 * addCargo -> the existing economy loop).
 */
import * as THREE from 'three';
import { type Vec3, type SalvageNode } from '@blackmarble/sim-core';

const POOL = 24;
const CANISTER_KM = 3;
const CANISTER_COLOR = 0x6f9a7a; // dull salvage green (sombre palette)

export class SalvageRenderer {
  private readonly meshes: THREE.Mesh[] = [];

  constructor(parent: THREE.Group) {
    const geo = new THREE.BoxGeometry(CANISTER_KM, CANISTER_KM, CANISTER_KM * 1.6);
    const mat = new THREE.MeshStandardMaterial({
      color: CANISTER_COLOR,
      emissive: CANISTER_COLOR,
      emissiveIntensity: 0.4,
      roughness: 0.6,
      metalness: 0.3,
    });
    for (let i = 0; i < POOL; i++) {
      const m = new THREE.Mesh(geo, mat);
      m.visible = false;
      this.meshes.push(m);
      parent.add(m);
    }
  }

  /** Place up to POOL active salvage nodes (floating-origin); hide the rest. */
  update(nodes: readonly SalvageNode[], shipWorldPos: Vec3, simTime: number): void {
    for (let i = 0; i < this.meshes.length; i++) {
      const m = this.meshes[i] as THREE.Mesh;
      const n = nodes[i];
      if (!n) {
        m.visible = false;
        continue;
      }
      m.visible = true;
      m.position.set(n.pos.x - shipWorldPos.x, n.pos.y - shipWorldPos.y, n.pos.z - shipWorldPos.z);
      m.rotation.x = simTime * 0.6 + i;
      m.rotation.y = simTime * 0.4 + i;
    }
  }
}
